import React from 'react';
import { Briefcase, BookOpen, Plus, Edit2, Phone, FileText, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { TabsContent } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

const CreatePostTab = ({
    jobForm, setJobForm, tuitionForm, setTuitionForm,
    handleCreateJob, handleCreateTuition,
    adminPostSearch, setAdminPostSearch,
    adminPostFilter, setAdminPostFilter,
    filteredAdminJobs, filteredAdminTuitions,
    setEditingAdminPost
}) => (
    <TabsContent value="create-post">
        <div className="grid lg:grid-cols-2 gap-6">
            {/* Create Job Card */}
            <Card className="border-l-4 border-l-blue-500">
                <CardHeader>
                    <CardTitle className="flex items-center">
                        <Briefcase className="h-5 w-5 mr-2 text-blue-600" />
                        Post a Job
                    </CardTitle>
                    <CardDescription>Create a job listing with contact info for tutors</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleCreateJob} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Job Title *</Label>
                                <Input placeholder="e.g., Mathematics Teacher" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} required />
                            </div>
                            <div>
                                <Label>Organization *</Label>
                                <Input placeholder="e.g., ABC School" value={jobForm.organization} onChange={(e) => setJobForm({ ...jobForm, organization: e.target.value })} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Location *</Label>
                                <Input placeholder="e.g., Mumbai, Maharashtra" value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} required />
                            </div>
                            <div>
                                <Label>Salary *</Label>
                                <Input placeholder="e.g., ₹30,000 - ₹50,000/month" value={jobForm.salary} onChange={(e) => setJobForm({ ...jobForm, salary: e.target.value })} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Latitude</Label>
                                <Input type="number" step="any" placeholder="e.g., 19.0760" value={jobForm.latitude} onChange={(e) => setJobForm({ ...jobForm, latitude: e.target.value })} />
                            </div>
                            <div>
                                <Label>Longitude</Label>
                                <Input type="number" step="any" placeholder="e.g., 72.8777" value={jobForm.longitude} onChange={(e) => setJobForm({ ...jobForm, longitude: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Job Type</Label>
                                <Select value={jobForm.job_type} onValueChange={(v) => setJobForm({ ...jobForm, job_type: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="full_time">Full Time</SelectItem>
                                        <SelectItem value="part_time">Part Time</SelectItem>
                                        <SelectItem value="freelance">Freelance</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Experience Required</Label>
                                <Input placeholder="e.g., 2-5 years" value={jobForm.experience} onChange={(e) => setJobForm({ ...jobForm, experience: e.target.value })} />
                            </div>
                        </div>
                        <div>
                            <Label>Subjects (comma separated) *</Label>
                            <Input placeholder="e.g., Mathematics, Physics, Chemistry" value={jobForm.subjects} onChange={(e) => setJobForm({ ...jobForm, subjects: e.target.value })} required />
                        </div>
                        <div>
                            <Label>Description *</Label>
                            <Textarea placeholder="Job description and requirements..." value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} rows={3} required />
                        </div>
                        <div className="border-t pt-4">
                            <h4 className="font-semibold mb-3 flex items-center">
                                <Phone className="h-4 w-4 mr-2 text-green-600" />
                                Contact Information
                            </h4>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <Label>Name *</Label>
                                    <Input placeholder="Contact person" value={jobForm.contact_name} onChange={(e) => setJobForm({ ...jobForm, contact_name: e.target.value })} required />
                                </div>
                                <div>
                                    <Label>Email *</Label>
                                    <Input type="email" placeholder="email@example.com" value={jobForm.contact_email} onChange={(e) => setJobForm({ ...jobForm, contact_email: e.target.value })} required />
                                </div>
                                <div>
                                    <Label>Phone *</Label>
                                    <Input placeholder="9876543210" value={jobForm.contact_phone} onChange={(e) => setJobForm({ ...jobForm, contact_phone: e.target.value })} required />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between border-t pt-4">
                            <div className="flex items-center space-x-2">
                                <Label>Unlock Price:</Label>
                                <Input type="number" className="w-20" value={jobForm.unlock_price} onChange={(e) => setJobForm({ ...jobForm, unlock_price: parseInt(e.target.value) || 0 })} min={0} />
                                <span className="text-sm text-gray-500">coins</span>
                            </div>
                            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                                <Plus className="h-4 w-4 mr-2" />
                                Create Job
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* Create Tuition Post Card */}
            <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                    <CardTitle className="flex items-center">
                        <BookOpen className="h-5 w-5 mr-2 text-green-600" />
                        Post a Tuition Requirement
                    </CardTitle>
                    <CardDescription>Create a tuition post for students looking for tutors</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleCreateTuition} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Category *</Label>
                                <Input placeholder="e.g., Class 10 Board Prep" value={tuitionForm.category} onChange={(e) => setTuitionForm({ ...tuitionForm, category: e.target.value })} required />
                            </div>
                            <div>
                                <Label>Board</Label>
                                <Select value={tuitionForm.board} onValueChange={(v) => setTuitionForm({ ...tuitionForm, board: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cbse">CBSE</SelectItem>
                                        <SelectItem value="icse">ICSE</SelectItem>
                                        <SelectItem value="state_board">State Board</SelectItem>
                                        <SelectItem value="international_baccalaureate">IB</SelectItem>
                                        <SelectItem value="igcse">IGCSE</SelectItem>
                                        <SelectItem value="none">None</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>City *</Label>
                                <Input placeholder="e.g., Delhi" value={tuitionForm.city} onChange={(e) => setTuitionForm({ ...tuitionForm, city: e.target.value })} required />
                            </div>
                            <div>
                                <Label>State *</Label>
                                <Input placeholder="e.g., Delhi" value={tuitionForm.state} onChange={(e) => setTuitionForm({ ...tuitionForm, state: e.target.value })} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Latitude</Label>
                                <Input type="number" step="any" placeholder="e.g., 28.6139" value={tuitionForm.latitude} onChange={(e) => setTuitionForm({ ...tuitionForm, latitude: e.target.value })} />
                            </div>
                            <div>
                                <Label>Longitude</Label>
                                <Input type="number" step="any" placeholder="e.g., 77.2090" value={tuitionForm.longitude} onChange={(e) => setTuitionForm({ ...tuitionForm, longitude: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Class Mode</Label>
                                <Select value={tuitionForm.class_mode} onValueChange={(v) => setTuitionForm({ ...tuitionForm, class_mode: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="online">Online</SelectItem>
                                        <SelectItem value="offline_student_place">At Student Place</SelectItem>
                                        <SelectItem value="offline_tutor_place">At Tutor Place</SelectItem>
                                        <SelectItem value="nearby_classes">Nearby Classes</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Budget (₹) *</Label>
                                <div className="flex space-x-2">
                                    <Input type="number" placeholder="5000" value={tuitionForm.budget_amount} onChange={(e) => setTuitionForm({ ...tuitionForm, budget_amount: parseInt(e.target.value) || 0 })} required />
                                    <Select value={tuitionForm.budget_type} onValueChange={(v) => setTuitionForm({ ...tuitionForm, budget_type: v })}>
                                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="monthly">Monthly</SelectItem>
                                            <SelectItem value="hourly">Hourly</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Label>Subjects (comma separated) *</Label>
                            <Input placeholder="e.g., Mathematics, Science" value={tuitionForm.subjects} onChange={(e) => setTuitionForm({ ...tuitionForm, subjects: e.target.value })} required />
                        </div>
                        <div>
                            <Label>Description *</Label>
                            <Textarea placeholder="Tuition requirements and preferences..." value={tuitionForm.description} onChange={(e) => setTuitionForm({ ...tuitionForm, description: e.target.value })} rows={3} required />
                        </div>
                        <div className="border-t pt-4">
                            <h4 className="font-semibold mb-3 flex items-center">
                                <Phone className="h-4 w-4 mr-2 text-green-600" />
                                Contact Information
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Name *</Label>
                                    <Input placeholder="Parent/Guardian name" value={tuitionForm.contact_name} onChange={(e) => setTuitionForm({ ...tuitionForm, contact_name: e.target.value })} required />
                                </div>
                                <div>
                                    <Label>Phone *</Label>
                                    <Input placeholder="9876543210" value={tuitionForm.contact_phone} onChange={(e) => setTuitionForm({ ...tuitionForm, contact_phone: e.target.value })} required />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between border-t pt-4">
                            <div className="flex items-center space-x-2">
                                <Label>Unlock Price:</Label>
                                <Input type="number" className="w-20" value={tuitionForm.unlock_price} onChange={(e) => setTuitionForm({ ...tuitionForm, unlock_price: parseInt(e.target.value) || 0 })} min={0} />
                                <span className="text-sm text-gray-500">coins</span>
                            </div>
                            <Button type="submit" className="bg-green-600 hover:bg-green-700">
                                <Plus className="h-4 w-4 mr-2" />
                                Create Tuition Post
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>

        {/* Admin's Posted Content */}
        <Card className="mt-6">
            <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <CardTitle className="flex items-center">
                            <FileText className="h-5 w-5 mr-2 text-purple-600" />
                            Your Posted Content
                        </CardTitle>
                        <CardDescription>Jobs and tuition posts created by admin</CardDescription>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search posts..."
                                value={adminPostSearch}
                                onChange={(e) => setAdminPostSearch(e.target.value)}
                                className="pl-8 w-40 h-9"
                            />
                        </div>
                        <Select value={adminPostFilter} onValueChange={setAdminPostFilter}>
                            <SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="jobs">Jobs</SelectItem>
                                <SelectItem value="tuitions">Tuitions</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {filteredAdminJobs.length === 0 && filteredAdminTuitions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No posts found. {adminPostSearch ? 'Try a different search.' : 'Use the forms above to create your first post.'}</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredAdminJobs.map((job) => (
                            <div key={job.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200 gap-3">
                                <div className="flex items-center space-x-3 min-w-0">
                                    <Briefcase className="h-5 w-5 text-blue-600 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="font-medium truncate">{job.title}</p>
                                        <p className="text-sm text-gray-500 truncate">{job.organization} • {job.location}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2 flex-wrap">
                                    <Badge className="bg-blue-100 text-blue-700">Job</Badge>
                                    <Badge variant="outline">{job.unlock_price} coins</Badge>
                                    <Badge className="bg-green-100 text-green-700">Approved</Badge>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8"
                                        onClick={() => setEditingAdminPost({
                                            type: 'job', id: job.id, title: job.title, organization: job.organization,
                                            location: job.location, salary: job.salary, description: job.description,
                                            experience: job.experience, unlock_price: job.unlock_price,
                                            job_type: job.type || job.job_type || 'full_time',
                                            subjects: job.subjects || [],
                                            admin_provided_contact: job.admin_provided_contact || {},
                                            latitude: job.latitude || '', longitude: job.longitude || ''
                                        })}
                                    >
                                        <Edit2 className="h-3.5 w-3.5 mr-1" />
                                        Edit
                                    </Button>
                                </div>
                            </div>
                        ))}
                        {filteredAdminTuitions.map((post) => (
                            <div key={post.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200 gap-3">
                                <div className="flex items-center space-x-3 min-w-0">
                                    <BookOpen className="h-5 w-5 text-green-600 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="font-medium truncate">{post.category}</p>
                                        <p className="text-sm text-gray-500 truncate">{post.city}, {post.state} • ₹{post.budget_amount}/{post.budget_type}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2 flex-wrap">
                                    <Badge className="bg-green-100 text-green-700">Tuition</Badge>
                                    <Badge variant="outline">{post.unlock_price} coins</Badge>
                                    <Badge className="bg-green-100 text-green-700">Approved</Badge>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8"
                                        onClick={() => setEditingAdminPost({
                                            type: 'tuition', id: post.id, category: post.category,
                                            description: post.description, city: post.city, state: post.state,
                                            budget_amount: post.budget_amount, budget_type: post.budget_type,
                                            unlock_price: post.unlock_price,
                                            board: post.board || 'cbse',
                                            class_mode: post.class_mode || 'offline_student_place',
                                            subjects: post.subjects || [],
                                            admin_provided_contact: post.admin_provided_contact || {},
                                            latitude: post.latitude || '', longitude: post.longitude || ''
                                        })}
                                    >
                                        <Edit2 className="h-3.5 w-3.5 mr-1" />
                                        Edit
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    </TabsContent>
);

export default CreatePostTab;
